import { Pane } from '@integration-app/ui/Layout'

import { cva } from 'styled-system/css'
import { Text } from 'ui-kit/text'

import useOrg from '../../../../../components/Orgs/org-context'

type UsageType = 'daily' | '30-day'

const usageAlertRecipe = cva({
  base: {
    padding: 2,
    textAlign: 'center',
    height: '8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textStyle: 'sm',
  },
  variants: {
    variant: {
      warning: {
        backgroundColor: 'warning.a9',
      },
      throttled: {
        backgroundColor: 'error.a9',
        color: 'error.fg',
        fontWeight: 'semibold',
      },
      stopped: {
        backgroundColor: 'error.a11',
        color: 'error.fg',
        fontWeight: 'semibold',
      },
    },
  },
})

export function UsageAlert() {
  const { org } = useOrg()

  const lastThirtyDayUsagePercent = org?.lastThirtyDayUsagePercent || 0
  const todayUsagePercent = org?.todayUsagePercent || 0

  // Return null if both usage percentages are below 80%
  if (lastThirtyDayUsagePercent < 80 && todayUsagePercent < 80) {
    return null
  }

  // Determine which usage type has the higher percentage
  const usageType: UsageType =
    lastThirtyDayUsagePercent >= todayUsagePercent ? '30-day' : 'daily'
  const usagePercent = Math.max(lastThirtyDayUsagePercent, todayUsagePercent)

  const usageAlertClassName = usageAlertRecipe({
    variant: getAlertStyle(usagePercent),
  })

  return (
    <Pane className={usageAlertClassName}>
      <Text>{getAlertMessage(usagePercent, usageType)}</Text>
    </Pane>
  )
}

function getAlertStyle(usagePercent: number) {
  if (usagePercent >= 300) {
    return 'stopped'
  }
  if (usagePercent >= 100) {
    return 'throttled'
  }
  return 'warning'
}

function getAlertMessage(usagePercent: number, usageType: UsageType) {
  const baseMessage = `Your organization reached ${Math.round(usagePercent)}% of the ${usageType} usage limit.`

  if (usagePercent >= 300) {
    return `${baseMessage} Your integrations were stopped. Please reach out to support for details.`
  }
  if (usagePercent >= 100) {
    return `${baseMessage} Your integration performance is throttled. Please reach out to support for details.`
  }
  return `${baseMessage} When it reaches 100%, your integrations performance can be limited. Please reach out to support for details.`
}
